import { useEffect, useRef, useState, type JSX } from "react";
import { useStore } from "../store.js";

export function DiagnosticsToggle(): JSX.Element {
  const open = useStore((s) => s.diagnosticsOpen);
  const toggle = useStore((s) => s.toggleDiagnostics);
  const wsReady = useStore((s) => s.wsReady);
  const [fps, setFps] = useState<number | null>(null);
  const [zoomReadout, setZoomReadout] = useState<string | null>(null);
  const frames = useRef(0);
  const lastTickAt = useRef(0);

  useEffect(() => {
    let raf = 0;
    lastTickAt.current = performance.now();

    // Sample zoom from the store at 1 Hz on purpose: the wheel handler may write
    // to it 60+ times per second, and we don't want the diagnostics panel to
    // re-render on every keystroke. The sample lives next to the FPS calc.
    const loop = (): void => {
      frames.current += 1;
      const now = performance.now();
      const elapsed = now - lastTickAt.current;
      if (elapsed >= 1000) {
        setFps(Math.round((frames.current * 1000) / elapsed));
        setZoomReadout(useStore.getState().zoom.toFixed(2));
        frames.current = 0;
        lastTickAt.current = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // Backgrounded tabs throttle rAF to ~1 Hz; reset the FPS readout when the
    // tab returns to foreground so a stale 1 fps doesn't linger after focus.
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        frames.current = 0;
        lastTickAt.current = performance.now();
        setFps(null);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (!open) {
    return (
      <button type="button" className="diag-pill" onClick={toggle} aria-label="Show diagnostics">
        DIAG
      </button>
    );
  }

  return (
    <section className="diagnostics" aria-label="Diagnostics">
      <div className="zoom-readout">
        <span>FPS</span>
        <strong>{fps ?? "--"}</strong>
      </div>
      <div className="zoom-readout">
        <span>ZOOM</span>
        <strong>{zoomReadout ?? "--"}</strong>
      </div>
      <div className="zoom-readout">
        <span>WS</span>
        <strong>{wsReady ? "ready" : "idle"}</strong>
      </div>
      <button type="button" className="diag-hide" onClick={toggle} aria-label="Hide diagnostics">
        HIDE
      </button>
    </section>
  );
}
